import { Node, mergeAttributes } from '@tiptap/core'

import { GeneralOptions } from '@/types'

import BackgroundBlockButton from './components/BackgroundBlockButton.vue'

function contentElement(dom: HTMLElement) {
  const children = Array.from(dom.children)
  return (children.find(child => child.classList.contains('background-block-content')) as HTMLElement) || dom
}

export interface BackgroundBlockOptions extends GeneralOptions<BackgroundBlockOptions> {
  HTMLAttributes: Record<string, unknown>
}

export interface BackgroundBlockAttributes {
  backgroundColor?: string
  borderColor?: string
  borderWidth?: string
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    backgroundBlock: {
      setBackgroundBlock: (options?: BackgroundBlockAttributes) => ReturnType
      toggleBackgroundBlock: (options?: BackgroundBlockAttributes) => ReturnType
      unsetBackgroundBlock: () => ReturnType
    }
  }
}

export const BackgroundBlock = Node.create<BackgroundBlockOptions>({
  name: 'backgroundBlock',
  group: 'block',
  content: '(block | columns)+',
  defining: true,
  draggable: false,
  addOptions() {
    return {
      ...this.parent?.(),
      HTMLAttributes: {},
      divider: false,
      spacer: false,
      toolbar: true,
      button: ({ editor }) => ({
        component: BackgroundBlockButton,
        componentProps: {
          action: (options?: unknown) => {
            if (typeof options === 'object' && options !== null) {
              editor
                .chain()
                .focus()
                .setBackgroundBlock(options as BackgroundBlockAttributes)
                .run()
            } else if (typeof options === 'undefined') {
              editor.chain().focus().unsetBackgroundBlock().run()
            }
          },
          editor,
          isActive: () => editor.isActive('backgroundBlock') || false,
          disabled: !editor.can().setBackgroundBlock(),
          tooltip: 'Background Block',
        },
      }),
    }
  },

  addAttributes() {
    return {
      backgroundColor: {
        default: 'transparent',
        parseHTML: element => element.getAttribute('data-background-color') || 'transparent',
        renderHTML: attributes => {
          if (!attributes.backgroundColor) {
            return {}
          }
          return {
            'data-background-color': attributes.backgroundColor,
          }
        },
      },
      borderColor: {
        default: null,
        parseHTML: element => element.getAttribute('data-border-color') || null,
        renderHTML: attributes => {
          if (!attributes.borderColor) {
            return {}
          }
          return {
            'data-border-color': attributes.borderColor,
          }
        },
      },
      borderWidth: {
        default: '5px',
        parseHTML: element => element.getAttribute('data-border-width') || '5px',
        renderHTML: attributes => {
          return {
            'data-border-width': attributes.borderWidth,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-background-block="true"]',
        priority: 100,
        contentElement,
      },
      {
        tag: 'div[data-background-block]',
        priority: 51,
        contentElement,
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    const backgroundColor = node.attrs.backgroundColor || 'transparent'
    const borderColor = node.attrs.borderColor
    const borderWidth = node.attrs.borderWidth || '5px'

    // Build the style string
    let styleString = `background-color: ${backgroundColor};`
    if (borderColor) {
      styleString += ` border-left: ${borderWidth} solid ${borderColor}; padding-left: 1rem;`
    }

    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-background-block': 'true',
        'data-background-color': backgroundColor,
        'data-border-color': borderColor,
        'data-border-width': borderWidth,
        class: 'background-block',
        style: styleString,
      }),
      [
        'div',
        {
          'data-background-edit': 'true',
          contenteditable: 'false',
          style: styleString,
        },
      ],
      ['div', { class: 'background-block-content' }, 0],
    ]
  },

  addCommands() {
    return {
      setBackgroundBlock:
        (options = {}) =>
        ({ commands, state }) => {
          const attrs: BackgroundBlockAttributes = {
            backgroundColor: options.backgroundColor || 'transparent',
            borderColor: options.borderColor || undefined,
            borderWidth: options.borderWidth || '5px',
          }

          // Check if we're already in a background block
          const { $from } = state.selection
          for (let d = $from.depth; d >= 0; d--) {
            if ($from.node(d).type.name === this.name) {
              // Update existing block's attributes
              return commands.updateAttributes(this.name, attrs)
            }
          }
          // Create new block
          return commands.wrapIn(this.name, attrs)
        },
      toggleBackgroundBlock:
        (options = {}) =>
        ({ commands }) => {
          const attrs: BackgroundBlockAttributes = {
            backgroundColor: options.backgroundColor || 'transparent',
            borderColor: options.borderColor || undefined,
            borderWidth: options.borderWidth || '5px',
          }
          return commands.toggleWrap(this.name, attrs)
        },
      unsetBackgroundBlock:
        () =>
        ({ commands }) => {
          return commands.lift(this.name)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Shift-h': () => this.editor.commands.toggleBackgroundBlock(),
    }
  },
})
